import api from './api';
import { usePreferencesStore } from '@/stores/preferences';
import type { AnalyticsResponse, OrganizationOverview, AnalyticsPeriod, BreakdownResponse, DateRange, Filters, EventLogResponse } from '@/types';

function getTimezone(): string {
  return usePreferencesStore.getState().timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function buildParams(
  period: AnalyticsPeriod,
  customDateRange?: DateRange | null,
  filters?: Filters,
): Record<string, string> {
  const params: Record<string, string> = {};

  if (customDateRange && period === 'custom') {
    params.start = customDateRange.start;
    params.end = customDateRange.end;
  } else {
    params.period = period;
  }

  // Use stored timezone preference, falling back to browser detection
  params.timezone = getTimezone();

  if (filters) {
    for (const [key, value] of Object.entries(filters)) {
      if (value) {
        params[`filter_${key}`] = value;
      }
    }
  }

  return params;
}

export const analyticsService = {
  async getSiteAnalytics(
    siteId: string,
    period: AnalyticsPeriod = '30d',
    customDateRange?: DateRange | null,
    filters?: Filters,
  ): Promise<AnalyticsResponse> {
    const params = buildParams(period, customDateRange, filters);
    const response = await api.get<AnalyticsResponse>(`/api/sites/${siteId}/analytics`, {
      params,
    });
    return response.data;
  },

  async getBreakdown(
    siteId: string,
    dimension: string,
    limit: number = 10,
    offset: number = 0,
    period: AnalyticsPeriod = '30d',
    customDateRange?: DateRange | null,
    filters?: Filters,
  ): Promise<BreakdownResponse> {
    const params = {
      ...buildParams(period, customDateRange, filters),
      dimension,
      limit: String(limit),
      offset: String(offset),
    };
    const response = await api.get<BreakdownResponse>(`/api/sites/${siteId}/analytics/breakdown`, {
      params,
    });
    return response.data;
  },

  async getOrganizationOverview(orgId: string, period: AnalyticsPeriod = '30d'): Promise<OrganizationOverview> {
    const response = await api.get<{ overview: OrganizationOverview }>(`/api/organizations/${orgId}/analytics/overview`, {
      params: { period, timezone: getTimezone() },
    });
    return response.data.overview;
  },

  async getRealtimeVisitors(
    siteId: string,
    minutes: number = 5,
  ): Promise<{ visitors: number; crawlers: number; bots: number }> {
    const response = await api.get<{ visitors: number; crawlers: number; bots: number }>(
      `/api/sites/${siteId}/analytics/realtime`,
      { params: { minutes } },
    );
    return {
      visitors: response.data.visitors,
      crawlers: response.data.crawlers ?? 0,
      bots: response.data.bots ?? 0,
    };
  },

  async getEventLog(
    siteId: string,
    period: AnalyticsPeriod = '30d',
    customDateRange?: DateRange | null,
    filters?: Filters,
    limit: number = 50,
    offset: number = 0,
    search?: string,
    classification?: string,
  ): Promise<EventLogResponse> {
    const params: Record<string, string> = {
      ...buildParams(period, customDateRange, filters),
      limit: String(limit),
      offset: String(offset),
    };
    if (search) params.search = search;
    if (classification) params.classification = classification;
    const response = await api.get<EventLogResponse>(`/api/sites/${siteId}/analytics/events`, { params });
    return response.data;
  },

  getEventLogExportUrl(
    siteId: string,
    period: AnalyticsPeriod = '30d',
    customDateRange?: DateRange | null,
    filters?: Filters,
    search?: string,
    classification?: string,
  ): string {
    const params: Record<string, string> = {
      ...buildParams(period, customDateRange, filters),
      export: 'csv',
    };
    if (search) params.search = search;
    if (classification) params.classification = classification;
    const searchParams = new URLSearchParams(params);
    const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';
    return `${API_BASE}/api/sites/${siteId}/analytics/events?${searchParams.toString()}`;
  },
};

export default analyticsService;
